// KnowledgeBank.State — one base assertion collection + one derived
// projection, both keyed structurally.
//
// Per `docs/model/bcs/knowledge-bank.md`. KB owns exactly one thing:
// the graph of typed relations between entities. It knows **no
// content** — no descriptions, no tags, no free-text — only
// connections (the dead `descriptions` / `tags` sub-collections were
// removed 2026-08-18, P6 step A: they had no writer anywhere;
// descriptions live on the entities they describe, tags reify to the
// Tag BC when a tagging feature lands). The two access patterns:
//
//   - `assertions` — global scan (`Map<AssertionKey, Assertion>` —
//     assertions addressable by triple).
//   - `derivedAssertions` — same shape as `assertions`, but the
//     entries are **inferred** from base assertions, not written
//     directly. Grundversion derives the transitive closure of
//     `prerequisite-of` only (Slice 3). `evolve` recomputes the
//     map after every event that mutates base `assertions`.
//
// **Derived assertions are not source of truth** — they are a
// projection ([[feedback_events_are_truth]]). Never persisted
// independently: on replay, `evolve` regenerates them from the
// base events alone. If an entry appears in `derivedAssertions`
// it is by definition *not* in `assertions` (the closure omits
// triples already present as base — avoids duplicate keys and
// makes "base vs derived" unambiguous at the read site).
//
// Keys are the branded string encodings from
// `../entityRef/key.ts` and `./assertionKey.ts` because JS `Map`
// uses object-reference equality — structural equality is what KB
// needs to make repeated writes idempotent.
//
// **Relations are entities.** The `relations` catalogue is the
// relation vocabulary as first-class id'd entities (IDD `bd4eafbc`);
// assertions reference relations by `RelationId`, so a rename/merge is
// a single event over a stable id, never a rewrite of every triple.
// `initial` seeds the catalogue (a runtime invariant / layer default —
// the seeded vocabulary always exists).
//
// **No per-triple lifecycle.** Assertions land and leave; relations
// have their own lifecycle (define/rename/merge/remove). Recovery is
// via replaying the event log per the invariant `assertions survive
// entity deletion` (§9). See `open-questions.md` §18 Q5.
export { initial, mkAssertion, mkDerivedAssertion };
import { seededRelations } from "./relation.js";
const mkAssertion = (subject, relation, object) => ({ subject, relation, object });
const mkDerivedAssertion = (subject, relation, object, derivedFrom) => ({ subject, relation, object, derivedFrom });
// The seeded relation catalogue is a runtime invariant — it always
// exists, so callers never guard "is the vocabulary loaded". Author-
// and agent-defined relations join it via `DefineRelation`.
const seededRelationsMap = new Map(seededRelations.map((r) => [r.id, r]));
const initial = {
    relations: seededRelationsMap,
    assertions: new Map(),
    derivedAssertions: new Map(),
    literals: new Map(),
};
//# sourceMappingURL=state.js.map